import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { maybePromptForPush } from '../services/softPushPrompt';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';
import { useContact } from '../contexts/ContactContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { addTradeOffer } from '../components/AddTradeOffer';
import { buildPedalSnapshotsPayload } from '../utils/tradeOfferPedalSnapshots';
import { MAX_INITIATOR_PEDALS } from '../utils/tradeUtils';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { createOfferSubmitTimer } from '../utils/offerSubmitTiming';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { clearOfferReturnViews } from '../hooks/useReturnViewState';
import AddressRequiredModal from '../components/AddressRequiredModal';
import { usePedalMarketValues } from '../hooks/usePedalMarketValues';
import { getTradeBalance, getListedBalance, formatBalanceLabel, getBalanceColors, getBalanceBadgeClass } from '../utils/tradeFairness';
import PriceIndicator from '../components/PriceIndicator';
import { rememberLoginReturn } from '../utils/loginReturn';
import InactiveOwnerNotice from '../components/InactiveOwnerNotice';
import ListingThumb from '../components/ListingThumb';
import { placeholderFor, isPlaceholderListing, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { allowsTrade, offerModeOf } from '../utils/offerMode';

const DirectOffer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openContact } = useContact();
  const targetPedalId = searchParams.get('targetPedalId');
  const preselectMyPedalId = searchParams.get('myPedalId');
  // Set when arriving from an interest the owner has answered. Their reply names
  // the pedals they'd trade for and may carry a note about terms, so this page
  // opens filtered to those pedals with the note in view — the answer they were
  // just given, on the screen where they act on it.
  const interestId = searchParams.get('interestId');
  // Set when arriving from Offer History's "Resend offer" on an expired
  // offer the user received: the mirrored terms (their pedal as the target,
  // the user's pedal(s) selected, same cash in the same direction), already
  // availability-checked at the click. Router state only, so a refresh
  // simply opens a normal blank offer page.
  const location = useLocation();
  const resendPrefill = location.state?.resendPrefill?.targetPedalId === targetPedalId
    ? location.state.resendPrefill
    : null;
  const [resendApplied, setResendApplied] = useState(false);
  const [interestReply, setInterestReply] = useState(null);
  const [showAllPedals, setShowAllPedals] = useState(false);
  const [closingInterest, setClosingInterest] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  
  const [targetPedal, setTargetPedal] = useState(null);
  const [userPedals, setUserPedals] = useState([]);
  const [selectedPedalIds, setSelectedPedalIds] = useState([]);
  const [offerType, setOfferType] = useState('');
  const [cashAmount, setCashAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // The inactive-owner notice points its tail at this button.
  const submitButtonRef = useRef(null);
  const [error, setError] = useState('');
  const [balanceSuggestion, setBalanceSuggestion] = useState(0);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [calculatingCosts, setCalculatingCosts] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  const { shippingAddress, loading: userLoading, firestoreUser } = useContext(UserContext);
  const { membershipTier, getCashFeePercentage: getMembershipCashFeePercentage, getTradeCompletionFee } = useContext(MembershipContext);
  // Reactive auth: on a native full-page reload (Propose Trade navigates via
  // window.location.href), auth.currentUser is null until Firebase restores the
  // persisted session. Reading it once (non-reactive) meant the loadData effect
  // didn't reliably re-run when the session restored, briefly dropping through
  // to the "No available pedals to offer" empty state before pedals loaded.
  // Tracking it in state keeps `loading` true until pedals actually load.
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [notLoggedIn, setNotLoggedIn] = useState(false);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [targetOwner, setTargetOwner] = useState(null);

  // Utility to check if address is complete (both parties need full addresses to ship)
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  // Auto-open the inline address modal when the user lands here without a
  // complete address. Same trigger as the original "Shipping Address Required"
  // modal — the difference is that the modal now contains the address form
  // itself instead of redirecting to /profile.
  useEffect(() => {
    if (!userLoading && !isAddressComplete(shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
  }, [shippingAddress, userLoading]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      // Fires with the definitive (post-restoration) state. Feed it into state
      // so the loadData effect re-runs once the session restores.
      setCurrentUser(user);
      if (!user) {
        setNotLoggedIn(true);
        setLoading(false);
      } else {
        setNotLoggedIn(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Restore form state from sessionStorage on mount, then immediately clear it
  useEffect(() => {
    const key = `directOffer_${targetPedalId}`;
    const saved = sessionStorage.getItem(`${key}_selectedPedals`);
    if (saved) {
      try { setSelectedPedalIds(JSON.parse(saved)); } catch { /* ignore */ }
      setOfferType(sessionStorage.getItem(`${key}_offerType`) || '');
      setCashAmount(sessionStorage.getItem(`${key}_cashAmount`) || '');
      setNote(sessionStorage.getItem(`${key}_note`) || '');
    }
    sessionStorage.removeItem(`${key}_selectedPedals`);
    sessionStorage.removeItem(`${key}_offerType`);
    sessionStorage.removeItem(`${key}_cashAmount`);
    sessionStorage.removeItem(`${key}_note`);
  }, [targetPedalId]);

  const saveFormStateToSession = () => {
    const key = `directOffer_${targetPedalId}`;
    sessionStorage.setItem(`${key}_selectedPedals`, JSON.stringify(selectedPedalIds));
    sessionStorage.setItem(`${key}_offerType`, offerType);
    sessionStorage.setItem(`${key}_cashAmount`, cashAmount);
    sessionStorage.setItem(`${key}_note`, note);
  };

  const togglePedalSelection = (pedalId) => {
    setSelectedPedalIds(prev => {
      if (prev.includes(pedalId)) {
        return prev.filter(id => id !== pedalId);
      }
      if (prev.length >= MAX_INITIATOR_PEDALS) return prev;
      return [...prev, pedalId];
    });
  };

  // The interestId is just a query param, so it gets checked rather than trusted.
  // It has to be this user's own signal, on the pedal they're actually looking
  // at, still sitting at the reply stage — otherwise the picks and the note
  // being shown are describing some other conversation.
  useEffect(() => {
    if (!interestId || !currentUser || !targetPedalId) return;
    let cancelled = false;
    getDoc(doc(db, 'listingInterest', interestId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        if (
          data.fromUserId !== currentUser.uid ||
          data.listingId !== targetPedalId ||
          data.status !== 'actioned'
        ) {
          return;
        }
        setInterestReply({
          pedalIds: Array.isArray(data.responsePedalIds) ? data.responsePedalIds : [],
          note: typeof data.responseNote === 'string' ? data.responseNote.trim() : ''
        });
      })
      .catch((err) => console.error('❌ Could not load the interest reply:', err));
    return () => {
      cancelled = true;
    };
  }, [interestId, currentUser, targetPedalId]);

  /**
   * Bow out of the conversation. Closing the interest clears it from this user's
   * offers page and tells the other side the offer isn't coming, so they aren't
   * left holding a set of picks for a trade that was never going to happen.
   */
  const handleCloseInterest = async () => {
    if (!interestId || closeSubmitting) return;
    setCloseSubmitting(true);
    try {
      await updateDoc(doc(db, 'listingInterest', interestId), {
        status: 'closed',
        closedAt: serverTimestamp()
      });
      // Same as the pass on the interest page: this button is far down the
      // pedal list, and that scroll offset survives the route change.
      scrollPageToTop();
      // Land at the TOP of Offers (the interest just left the list), keeping
      // the tab: opts out of the Offers page's position restore.
      navigate('/trade-center', { state: { skipViewRestore: true } });
    } catch (err) {
      console.error('❌ Could not close the interest:', err);
      setError('Could not do that. Please try again.');
      setCloseSubmitting(false);
      setClosingInterest(false);
    }
  };

  // Narrowed to what's actually still offerable: a pedal they picked may have
  // sold or gone into another trade since they picked it.
  const flaggedPedalIds = useMemo(() => {
    const ids = interestReply?.pedalIds || [];
    return ids.filter((id) => userPedals.some((p) => p.id === id));
  }, [interestReply, userPedals]);
  const hasInterestPicks = flaggedPedalIds.length > 0;

  // Filter is a starting point, not a cage: a pedal they didn't flag may still
  // be the one that closes the trade, so "show all" is always a click away.
  const visiblePedals = useMemo(() => {
    if (showAllPedals || !hasInterestPicks) return userPedals;
    return userPedals.filter((p) => flaggedPedalIds.includes(p.id));
  }, [userPedals, flaggedPedalIds, hasInterestPicks, showAllPedals]);

  // Reverb market values for every pedal on screen, so the fairness read is
  // based on what these actually sell for rather than on seller-set asking prices.
  const pricingPedals = useMemo(
    () => (targetPedal ? [...userPedals, targetPedal] : userPedals),
    [userPedals, targetPedal]
  );
  const { marketTiers } = usePedalMarketValues(pricingPedals);
  const targetTier = targetPedal ? marketTiers.get(targetPedal.id) : null;
  const marketBalance = useMemo(
    () => getTradeBalance(selectedPedalIds.map((id) => marketTiers.get(id)), targetTier),
    [selectedPedalIds, marketTiers, targetTier]
  );

  // Calculate balance suggestion when pedals are selected
  useEffect(() => {
    if (selectedPedalIds.length > 0 && targetPedal && userPedals.length > 0) {
      const combinedValue = selectedPedalIds.reduce((sum, id) => {
        const pedal = userPedals.find(p => p.id === id);
        return sum + (pedal ? Number(pedal.price) : 0);
      }, 0);
      setBalanceSuggestion(combinedValue - Number(targetPedal.price));
    } else {
      setBalanceSuggestion(0);
    }
  }, [selectedPedalIds, targetPedal, userPedals]);

  // Scroll to offer details section when first pedal is selected
  useEffect(() => {
    if (selectedPedalIds.length === 1) {
      setTimeout(() => {
        const el = document.getElementById('offer-details-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [selectedPedalIds]);

  // Pre-select initiator pedal when arriving from suggested-trades strip.
  useEffect(() => {
    if (!preselectMyPedalId || userPedals.length === 0 || selectedPedalIds.length > 0) return;
    if (userPedals.some((p) => p.id === preselectMyPedalId)) {
      setSelectedPedalIds([preselectMyPedalId]);
    }
  }, [preselectMyPedalId, userPedals, selectedPedalIds.length]);

  // Apply the "Resend offer" pre-fill once the user's available pedals are
  // loaded. userPedals holds only available listings, so any pre-filled
  // pedal that became unavailable since the click is dropped here too. A
  // sessionStorage restore (user coming back to this form) wins: it is the
  // user's own in-progress edit.
  useEffect(() => {
    if (!resendPrefill || resendApplied || loading) return;
    setResendApplied(true);
    if (selectedPedalIds.length > 0) return;
    const availableIds = new Set(userPedals.map((p) => p.id));
    const pick = (resendPrefill.myPedalIds || [])
      .filter((id) => availableIds.has(id))
      .slice(0, MAX_INITIATOR_PEDALS);
    if (pick.length === 0) return;
    setSelectedPedalIds(pick);
    if (resendPrefill.offerType) setOfferType(resendPrefill.offerType);
    if (resendPrefill.cashAmount) setCashAmount(formatCurrencyInput(String(resendPrefill.cashAmount)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendPrefill, resendApplied, loading, userPedals]);

  // Load target pedal and user's available pedals
  useEffect(() => {
    const loadData = async () => {
      if (!targetPedalId) {
        setLoading(false);
        return;
      }
      if (!currentUser) {
        // Auth session not restored yet (common on a fresh load, and especially
        // in the native WebView). Stay in the loading state instead of falling
        // through to the "No Available Pedals" empty screen. The
        // onAuthStateChanged listener covers the genuinely-logged-out case; once
        // the session restores, currentUser populates and this effect re-runs.
        return;
      }

      setLoading(true);

      try {
        // Load target pedal
        const targetPedalDoc = await getDoc(doc(db, 'listings', targetPedalId));
        if (!targetPedalDoc.exists()) {
          if (!currentUser) {
            // Save current URL so user can return after login
            sessionStorage.setItem('returnAfterLogin', window.location.pathname);
            setError('Please log in to view this pedal. Click here to log in: /login');
          } else {
            setError('Target pedal not found');
          }
          setLoading(false);
          return;
        }
        const targetPedalData = { id: targetPedalDoc.id, ...targetPedalDoc.data() };
        if (!allowsTrade(targetPedalData)) {
          // Before setTargetPedal: the error screen only renders while the
          // target is unset (review 2026-10-20).
          setError('This pedal is for sale only. The owner is not taking trade offers on it.');
          setLoading(false);
          return;
        }
        setTargetPedal(targetPedalData);

        // Load target pedal owner info
        if (targetPedalData.ownerId) {
          // PII isolation: read sanitized public mirror (only username/photo used in render).
          const ownerDoc = await getDoc(doc(db, 'publicProfiles', targetPedalData.ownerId));
          if (ownerDoc.exists()) {
            setTargetOwner({ id: ownerDoc.id, ...ownerDoc.data() });
          }
        }

        // Load user's active pedals (not in trade and active)
        const userPedalsQuery = query(
          collection(db, 'listings'),
          where('ownerId', '==', currentUser.uid),
          where('active', '==', true)
        );
        
        const userPedalsSnapshot = await getDocs(userPedalsQuery);
        const availablePedals = [];
        
        userPedalsSnapshot.forEach((doc) => {
          const pedalData = doc.data();
          // Only include pedals that are not in trade, and not sale-only
          // (a sale-only pedal is never given in a trade; plan §5).
          if (!pedalData.inTrade && offerModeOf(pedalData) !== 'sell') {
            availablePedals.push({ id: doc.id, ...pedalData });
          }
        });
        
        setUserPedals(availablePedals);
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load pedal data');
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, targetPedalId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    const timer = createOfferSubmitTimer('direct_offer', { listingId: targetPedalId });

    try {
      // Block if address is incomplete
      if (!isAddressComplete(shippingAddress)) {
        setShowAddressModal(true);
        setSubmitting(false);
        return;
      }

      if (selectedPedalIds.length === 0) {
        throw new Error('Please select at least one pedal to offer');
      }

      if (!targetPedal) {
        throw new Error('Target pedal not found');
      }

      const listingSnaps = await Promise.all([
        getDoc(doc(db, 'listings', targetPedalId)),
        ...selectedPedalIds.map((pedalId) => getDoc(doc(db, 'listings', pedalId))),
      ]);
      const targetPedalDoc = listingSnaps[0];
      const initiatorPedalDocs = listingSnaps.slice(1);

      if (!targetPedalDoc.exists()) {
        throw new Error('Target pedal no longer exists');
      }

      const latestTargetPedalData = targetPedalDoc.data();
      if (latestTargetPedalData.ownerId === currentUser.uid) {
        throw new Error("You can't make an offer on your own listing");
      }
      if (latestTargetPedalData.inTrade) {
        throw new Error('This pedal is no longer available for trade offers. It may have been accepted in another trade.');
      }
      if (!allowsTrade(latestTargetPedalData)) {
        throw new Error('This pedal is for sale only. The owner is not taking trade offers on it.');
      }

      const initiatorPedalPrices = {};
      const initiatorPedalDetails = {};
      const initiatorListingSnapshots = [];
      for (let i = 0; i < selectedPedalIds.length; i++) {
        const pedalId = selectedPedalIds[i];
        const pedalDoc = initiatorPedalDocs[i];
        if (!pedalDoc.exists()) {
          throw new Error('One of your selected pedals no longer exists');
        }
        const pedalData = pedalDoc.data();
        if (pedalData.inTrade) {
          throw new Error(`Your ${pedalData.make} ${pedalData.model} is no longer available. It may have been accepted in another trade.`);
        }
        initiatorPedalPrices[pedalId] = Number(pedalData.price || 0);
        initiatorPedalDetails[pedalId] = {
          name: `${pedalData.make} ${pedalData.model}`,
          imageUrl: pedalData.images?.top || null,
          placeholderUrl: pedalData.placeholderImage?.url || null,
          condition: pedalData.condition || null,
        };
        initiatorListingSnapshots.push({ id: pedalId, ...pedalData });
      }

      timer.mark('freshness_reads', { pedalCount: selectedPedalIds.length });

      let validatedCashAmount = 0;
      if (['ask', 'offer'].includes(offerType)) {
        const rawAmount = Number(cashAmount);
        if (isNaN(rawAmount) || rawAmount < 0) {
          throw new Error('Please enter a valid cash amount (must be positive)');
        }
        if (rawAmount > 2000) {
          throw new Error('Cash amount cannot exceed $2,000');
        }
        validatedCashAmount = Math.abs(rawAmount);
        validatedCashAmount = Math.round(validatedCashAmount * 100) / 100;
      }

      const safeNote = note.trim().slice(0, 500);

      const responderPedalDetail = {
        name: `${latestTargetPedalData.make} ${latestTargetPedalData.model}`,
        imageUrl: latestTargetPedalData.images?.top || null,
        placeholderUrl: latestTargetPedalData.placeholderImage?.url || null,
        condition: latestTargetPedalData.condition || null,
      };

      const pedalSnapshots = buildPedalSnapshotsPayload(
        initiatorListingSnapshots,
        { id: targetPedalId, ...latestTargetPedalData }
      );

      const tradeId = await addTradeOffer({
        matchId: null,
        initiatorId: currentUser.uid,
        initiatorEmail: currentUser.email,
        responderId: latestTargetPedalData.ownerId,
        initiatorPedalIds: selectedPedalIds,
        responderPedalId: targetPedalId,
        cashAdjustmentType: offerType,
        cashAdjustmentAmount: validatedCashAmount,
        note: safeNote,
        initiatorPedalPrices,
        responderPedalPrice: Number(latestTargetPedalData.price || 0),
        initiatorPedalDetails,
        responderPedalDetail,
        pedalSnapshots,
        sourceInterestId: interestId || null,
      });

      timer.mark('write_ack', { tradeOfferId: tradeId });

      // Retire the interest that led here. Left open, its "Ready for an offer"
      // card would sit on this page next to the offer it produced, still
      // counting toward the badge and still inviting a second offer. 'offered'
      // rather than 'closed' because closing tells the other side you passed,
      // and the offer notification is already on its way to them.
      if (interestId) {
        await updateDoc(doc(db, 'listingInterest', interestId), {
          status: 'offered',
          closedAt: serverTimestamp(),
          resultingTradeOfferId: tradeId,
        }).catch((err) => console.error('❌ Could not retire the interest:', err));
        void recordOfferIntentEvent(OFFER_INTENT_KINDS.INTEREST_TO_OFFER, {
          listingId: targetPedalId,
          interestId,
          tradeOfferId: tradeId
        });
      }

      void recordOfferIntentEvent(OFFER_INTENT_KINDS.TRADE_OFFER_SUBMITTED, {
        listingId: targetPedalId,
        tradeOfferId: tradeId,
        source: 'direct_offer',
      });

      // Contextual soft-ask for push permission (native-only, no-op on web).
      maybePromptForPush('make_offer');

      const key = `directOffer_${targetPedalId}`;
      sessionStorage.removeItem(`${key}_selectedPedals`);
      sessionStorage.removeItem(`${key}_offerType`);
      sessionStorage.removeItem(`${key}_cashAmount`);
      sessionStorage.removeItem(`${key}_note`);

      // A new offer changes what matters on Offers / Offer History, so the
      // user lands fresh there instead of at a saved row that may be gone.
      clearOfferReturnViews();

      // Navigate to the offer page
      timer.mark('done');
      navigate(`/offer/${tradeId}`, { state: { justCreated: true } });
    } catch (err) {
      timer.fail(err);
      console.error('Error submitting offer:', err);
      setError(err.message || 'Failed to submit offer');
      setSubmitting(false);
    }
  };


  const calculateCostBreakdown = (amount, type) => {
    if (!type) {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
      return;
    }

    setCalculatingCosts(true);
    
    try {
      const cashValue = parseFloat(amount);
      const tradeCompletionFee = getTradeCompletionFee(selectedPedalIds.length);
      const cashFeePercentage = getMembershipCashFeePercentage();
      
      let totalYouPay = 0;
      let totalYouReceive = 0;
      let cashExchangeFee = 0;
      
      if (type === 'offer') {
        cashExchangeFee = Math.round(cashValue * (cashFeePercentage / 100) * 100) / 100;
        totalYouPay = cashValue + tradeCompletionFee + cashExchangeFee;
      } else if (type === 'ask') {
        cashExchangeFee = 0;
        totalYouReceive = cashValue - tradeCompletionFee;
      } else {
        totalYouPay = tradeCompletionFee;
      }
      
      setCostBreakdown({
        cashValue,
        tradeCompletionFee,
        pedalCount: selectedPedalIds.length,
        cashExchangeFee,
        cashFeePercentage,
        totalYouPay,
        totalYouReceive,
        offerType: type
      });
      setShowCostBreakdown(true);
    } catch (error) {
      console.error('Error calculating cost breakdown:', error);
    } finally {
      setCalculatingCosts(false);
    }
  };

  useEffect(() => {
    if (!offerType) {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
      return;
    }
    const amount = parseFloat(cashAmount) || 0;
    const debounceTimer = setTimeout(() => {
      calculateCostBreakdown(amount, offerType);
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [cashAmount, offerType, membershipTier, selectedPedalIds.length]);

  // Show/hide "Back to Top" button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down more than 300px
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to Make an Offer</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to make a trade offer.</p>
          {/* Return here (with ?targetPedalId) after login: email links land on
              this page. Saved on the click only; see utils/loginReturn. */}
          <Link to="/login" onClick={() => rememberLoginReturn()} style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center">Loading trade details...</div>
      </div>
    );
  }

  if (error && !targetPedal) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">{error}</div>
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (userPedals.length === 0) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-center mb-6">Propose a trade</h1>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Available Pedals</h3>
            <p className="text-yellow-700 mb-2" style={{ fontSize: '0.9rem' }}>Pedals set to Sale only cannot be offered in a trade.</p>
            <p className="text-yellow-700 mb-4">
              You need to have at least one active pedal that's not currently in a trade to make an offer.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              <button 
                onClick={() => navigate('/addpedal')}
                style={{
                  backgroundColor: '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '8px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Add a Pedal
              </button>
              <button 
                onClick={() => navigate(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#5A9ECB'
                }}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="direct-offer-page" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', padding: '0 1rem' }}>
      <style jsx>{`
        @media (max-width: 768px) {
          .direct-offer-page {
            padding: 0 0.5rem !important;
          }
          .direct-offer-card {
            padding: 1rem !important;
            margin-left: 0.25rem !important;
            margin-right: 0.25rem !important;
          }
          .direct-offer-hero-image {
            max-width: 100% !important;
          }
          .direct-offer-note-textarea {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .direct-offer-card .bg-gray-50 {
            overflow: hidden !important;
          }
          .direct-offer-comparison {
            gap: 0.75rem !important;
          }
          /* Mobile only: the price check's fixed 210px would otherwise become a
             floor on this column and push the Your Pedals column narrower, so
             let the column size from its text and the bar follow it. Desktop
             keeps the component's natural width. */
          .their-pedal-info {
            min-width: 0 !important;
          }
          .their-pedal-price-check > div {
            width: 100% !important;
          }
          /* Middle "Fair" chip sizes to its own label rather than growing with
             the others, handing that space to "Good value" and "High" so
             neither is pinched at this narrow width. */
          .their-pedal-price-check > div > div > div:nth-child(2) {
            flex: 0 0 auto !important;
            padding: 0 6px !important;
          }
          .direct-offer-placeholder {
            max-width: 140px !important;
          }
          .your-pedals-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.75rem !important;
            padding: 0 !important;
          }
          .back-to-top-btn {
            bottom: 100px !important;
          }
          .bg-gray-100.border-t {
            padding-bottom: 100px !important;
          }
        }
        @media (min-width: 769px) {
          .direct-offer-hero-image {
            max-width: 100% !important;
          }
          .direct-offer-note-textarea {
            width: 100% !important;
            min-width: auto !important;
          }
          .offer-details-heading {
            padding-top: 0.75rem !important;
          }
          .their-pedal-info {
            padding-left: 1.5rem !important;
          }
          .their-pedal-info .their-pedal-owner-name {
            font-size: 1.1rem !important;
          }
          .their-pedal-info .their-pedal-make {
            font-size: 1.15rem !important;
          }
          .their-pedal-info .their-pedal-model {
            font-size: 1.15rem !important;
          }
          .their-pedal-info .their-pedal-details {
            font-size: 0.95rem !important;
          }
          .their-pedal-info .their-pedal-link {
            font-size: 0.95rem !important;
          }
          .your-pedals-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          .back-to-top-btn {
            right: 160px !important;
          }
        }
        @media (max-width: 1100px) and (min-width: 769px) {
          .your-pedals-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 0' }}>
        <h1 className="font-bold text-center mb-4" style={{ fontSize: '1.75rem' }}>Propose a trade</h1>

        {/* White bounding box: target pedal + offer details + buttons */}
        <div className="direct-offer-card" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          padding: '1.5rem',
          maxWidth: '560px',
          margin: '0 auto 1.5rem auto'
        }}>
          {/* Target Pedal Hero Image + Details below (left-aligned) */}
          {targetPedal && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div className="direct-offer-hero-image" style={{ margin: '0 auto' }}>
                {targetPedal.images?.top ? (
                  <img
                    src={targetPedal.images.top}
                    alt={`${targetPedal.make} ${targetPedal.model}`}
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '15px' }}
                  />
                ) : targetPedal.placeholderImage || isPlaceholderListing(targetPedal) ? (
                  // Photo-less listing: its stand-in (Reverb catalog photo or ours) with the tag.
                  <div style={{ position: 'relative' }}>
                    <ListingThumb
                      src={placeholderFor(targetPedal).url}
                      fallbackSrc={SKETCH_PLACEHOLDER_URL}
                      alt={`${targetPedal.make} ${targetPedal.model}`}
                      style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '15px', display: 'block', background: '#F5F1E8' }}
                    />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '12px', fontWeight: 700, padding: '5px 10px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>Photos coming</div>
                  </div>
                ) : (
                  <div style={{ width: '100%', aspectRatio: '1', backgroundColor: '#f3f4f6', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="text-gray-400">No Image</span>
                  </div>
                )}
              </div>

              {/* Two-column row: Their Pedal details (left) + Your Selected Pedal preview (right) */}
              <div id="offer-details-section" className="direct-offer-comparison" style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', alignItems: 'flex-start' }}>
                {/* Their Pedal Info - Left */}
                <div className="their-pedal-info" style={{ flex: 1, textAlign: 'left' }}>
                  <h4 className="font-semibold mb-2" style={{ fontSize: '0.9rem', color: '#6b7280' }}>Their Pedal</h4>

                  {/* Owner profile icon + username */}
                  {targetOwner && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <button
                        onClick={() => navigate(`/profile/${targetOwner.id}`)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        <img
                          src={targetOwner.photoURL || '/images/default-avatar.png'}
                          alt='Owner Avatar'
                          style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #ccc' }}
                        />
                      </button>
                      <button
                        className="their-pedal-owner-name"
                        onClick={() => navigate(`/profile/${targetOwner.id}`)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito, sans-serif', textAlign: 'left' }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {targetOwner.username || 'User'}
                      </button>
                    </div>
                  )}

                  <div className="their-pedal-make" style={{ fontWeight: 'bold', color: '#374151', fontSize: '1rem', lineHeight: '1.3' }}>
                    {targetPedal.make}
                  </div>
                  <div className="their-pedal-model" style={{ fontWeight: 'bold', color: '#6b7280', fontSize: '1rem', lineHeight: '1.3', marginBottom: '0.25rem' }}>
                    {targetPedal.model}
                  </div>
                  <div className="their-pedal-details" style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: '1.6' }}>
                    <div><strong>Condition:</strong> {targetPedal.condition}</div>
                    <div><strong>Price:</strong> ${targetPedal.price}</div>
                  </div>
                  {/* Same Good value / Fair / High bar as the listing page.
                      Fetches on demand, so it still appears when this pedal
                      has no cached pedalPricing doc yet. */}
                  {/* Left at its natural size here; only the mobile rule below
                      makes it fluid, so desktop matches the listing page. */}
                  <div className="their-pedal-price-check" style={{ margin: '8px 0 4px' }}>
                    <PriceIndicator
                      compact
                      make={targetPedal.make}
                      model={targetPedal.model}
                      condition={targetPedal.condition}
                      price={Number(targetPedal.price) || 0}
                      decade={targetPedal.decade}
                      listingId={targetPedal.id}
                    />
                  </div>
                  <Link
                    to={`/listing/${targetPedal.id}`}
                    onClick={saveFormStateToSession}
                    className='their-pedal-link hover:underline'
                    style={{ color: '#5A9ECB', fontSize: '0.85rem', marginTop: '4px', display: 'inline-block' }}
                  >
                    View Full Listing
                  </Link>
                </div>

                {/* Your Selected Pedals Preview - Right (2x2 Grid) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h4 className="font-semibold mb-2" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    Your Pedal{selectedPedalIds.length !== 1 ? 's' : ''}
                    {selectedPedalIds.length > 0 && (
                      <span style={{ fontWeight: 'normal', color: '#9ca3af', marginLeft: '4px' }}>
                        ({selectedPedalIds.length}/{MAX_INITIATOR_PEDALS})
                      </span>
                    )}
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '6px',
                    maxWidth: '180px',
                    margin: '0 auto'
                  }}>
                    {Array.from({ length: MAX_INITIATOR_PEDALS }).map((_, idx) => {
                      const pedalId = selectedPedalIds[idx];
                      const pedal = pedalId ? userPedals.find(p => p.id === pedalId) : null;
                      return (
                        <div
                          key={pedal ? pedalId : `empty-${idx}`}
                          style={{
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: pedal ? '2px solid #3b82f6' : '2px dashed #d1d5db',
                            backgroundColor: pedal ? 'transparent' : '#f9fafb',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {pedal ? (
                            <>
                              <img
                                src={pedal.images?.top || placeholderFor(pedal).url}
                                alt={`${pedal.make} ${pedal.model}`}
                                onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  opacity: pedal.images?.top ? 1 : 0.85
                                }}
                              />
                              <div style={{
                                position: 'absolute', top: '3px', left: '3px',
                                backgroundColor: '#3b82f6', color: 'white',
                                borderRadius: '50%', width: '18px', height: '18px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.65rem', fontWeight: 'bold'
                              }}>
                                {idx + 1}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: '#d1d5db', fontSize: '1.2rem' }}>+</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedPedalIds.length === 0 && (
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '6px', lineHeight: '1.3' }}>
                      Select up to {MAX_INITIATOR_PEDALS} pedals from below
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Offer Details Section */}
          <div>
            <div className='bg-gray-50 p-4 rounded-lg border'>
              {!userLoading && !isAddressComplete(shippingAddress) && (
                <p style={{ color: '#dc2626', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.4', textAlign: 'center' }}>
                  A shipping address is required before submitting this offer.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
                  >
                    Add address
                  </button>
                </p>
              )}
              <h4 className='font-semibold mb-1 offer-details-heading' style={{ textAlign: 'left' }}>Offer Details</h4>

            {selectedPedalIds.length > 0 && (
              <div style={{ marginBottom: '12px', textAlign: 'left' }}>
                <div>
                  <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginRight: '8px' }}>By listed prices</span>
                  <span style={{ color: balanceSuggestion === 0 ? '#16a34a' : '#C9A227', fontWeight: '600', fontSize: '0.9rem' }}>
                    {balanceSuggestion === 0 ? 'Even trade!' : balanceSuggestion < 0 ? 'Suggested cash offer' : 'Suggested cash ask'}
                  </span>
                  {balanceSuggestion !== 0 && (
                    <span style={{ color: '#C9A227', fontWeight: '700', fontSize: '0.9rem', marginLeft: '8px' }}>${Math.abs(balanceSuggestion)}</span>
                  )}
                </div>
                {marketBalance && (
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginRight: '8px' }}>By Reverb market</span>
                    <span style={{ color: getBalanceColors(marketBalance).text, fontWeight: '700', fontSize: '0.9rem' }}>
                      {formatBalanceLabel(marketBalance)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
              {[
                { value: 'straight', label: 'Straight Trade', desc: 'No cash involved' },
                { value: 'ask', label: 'Ask for additional $', desc: 'They pay you cash' },
                { value: 'offer', label: 'Offer additional $', desc: 'You pay them cash' }
              ].map(opt => (
                <label
                  key={opt.value}
                  onClick={() => setOfferType(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                    border: offerType === opt.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    backgroundColor: offerType === opt.value ? '#eff6ff' : '#f9fafb',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="radio"
                      name="offerType"
                      value={opt.value}
                      checked={offerType === opt.value}
                      onChange={(e) => setOfferType(e.target.value)}
                      style={{ flexShrink: 0, accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827' }}>{opt.label}</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{opt.desc}</span>
                </label>
              ))}
            </div>

            {(offerType === 'offer' || offerType === 'ask') && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>Amount ($)</label>
                  <input
                    type="text"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(formatCurrencyInput(e.target.value))}
                    placeholder="0"
                    required
                    className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                    style={{ flex: 1, padding: '8px 12px', fontSize: '0.95rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                </div>
              </div>
            )}

            {calculatingCosts && (
              <div className="text-center text-sm text-gray-500 py-2">Calculating costs...</div>
            )}

            {showCostBreakdown && costBreakdown && !calculatingCosts && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
                <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px', textAlign: 'left' }}>Your cost breakdown</h5>
                <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span>Platform fee</span>
                    <span>
                      ${costBreakdown.tradeCompletionFee.toFixed(2)}
                      {costBreakdown.pedalCount > 1 && (
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}> ($3 + $1/addtl pedal)</span>
                      )}
                    </span>
                  </div>
                  {costBreakdown.offerType === 'offer' && costBreakdown.cashValue > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>Cash payment</span>
                        <span>${costBreakdown.cashValue.toFixed(2)}</span>
                      </div>
                      {costBreakdown.cashExchangeFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                          <span>Cash processing fee ({costBreakdown.cashFeePercentage}%)</span>
                          <span>${costBreakdown.cashExchangeFee.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {costBreakdown.offerType === 'ask' && costBreakdown.cashValue > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#16a34a' }}>
                      <span>Cash you'll receive</span>
                      <span>+${costBreakdown.cashValue.toFixed(2)}</span>
                    </div>
                  )}
                  {(() => {
                    const netReceive = costBreakdown.offerType === 'ask' && costBreakdown.totalYouReceive > 0;
                    const displayAmount = netReceive
                      ? costBreakdown.totalYouReceive
                      : (costBreakdown.offerType === 'ask' ? Math.abs(costBreakdown.totalYouReceive) : costBreakdown.totalYouPay);
                    return (
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                        <span>Total you'll {netReceive ? 'receive' : 'pay'}</span>
                        <span style={{ color: netReceive ? '#15803d' : '#111827' }}>
                          {netReceive ? '+' : ''}${displayAmount.toFixed(2)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="mb-4" style={{ overflow: 'hidden' }}>
              <div style={{ marginBottom: '0.5rem', textAlign: 'left' }}>
                <label className="font-semibold">Note (optional):</label>
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a short note..."
                maxLength={500}
                rows={4}
                className="w-full border border-gray-300 focus:border-blue-500 focus:outline-none resize-none direct-offer-note-textarea"
                style={{ padding: '12px', fontSize: '16px', borderRadius: '6px', lineHeight: '1.5', boxSizing: 'border-box', width: '100%' }}
              />
            </div>

            {error && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded">
                <p className="text-red-600 text-xs">{error}</p>
              </div>
            )}
          </div>

          {/* Reassurance copy ABOVE the CTA — same line as below, intentionally bracketing the buttons. */}
          <p
            style={{
              textAlign: 'center',
              margin: '1rem auto 0.5rem',
              maxWidth: '420px',
              fontSize: '0.85rem',
              color: '#6B7280',
              fontFamily: 'Nunito, sans-serif',
              lineHeight: '1.4'
            }}
          >
            Sending an offer starts the conversation.
          </p>

          {/* Heads-up when the pedal's owner hasn't been on in a while
              (30+ days by their last active week; see utils/inactivity). */}
          <InactiveOwnerNotice ownerName={targetOwner?.username} lastActiveWeek={targetOwner?.lastActiveWeek} anchorRef={submitButtonRef} />

          {/* Submit / Cancel Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '0.5rem' }}>
            <button
              ref={submitButtonRef}
              onClick={handleSubmit}
              disabled={submitting || selectedPedalIds.length === 0 || !offerType || !isAddressComplete(shippingAddress)}
              style={{
                backgroundColor: (submitting || selectedPedalIds.length === 0 || !offerType || !isAddressComplete(shippingAddress)) ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '8px 24px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '1rem',
                cursor: (submitting || selectedPedalIds.length === 0 || !offerType || !isAddressComplete(shippingAddress)) ? 'not-allowed' : 'pointer',
                opacity: (submitting || selectedPedalIds.length === 0 || !offerType || !isAddressComplete(shippingAddress)) ? 0.6 : 1,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => { if (!submitting && selectedPedalIds.length > 0 && offerType && isAddressComplete(shippingAddress)) e.target.style.opacity = '0.8'; }}
              onMouseLeave={(e) => { if (!submitting && selectedPedalIds.length > 0 && offerType && isAddressComplete(shippingAddress)) e.target.style.opacity = '1'; }}
            >
              {submitting ? 'Submitting...' : 'Submit Offer'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                backgroundColor: '#E5E7EB',
                color: '#374151',
                border: 'none',
                borderRadius: '9999px',
                padding: '8px 24px',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '0.95rem',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Cancel
            </button>
          </div>
        </div>
        </div>

        {/* Your Pedals Grid Section */}
        <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <h3 className="font-bold text-center mb-4" style={{ fontSize: '1.25rem', color: '#374151' }}>Your Pedals</h3>
          <p className="text-center text-gray-500 mb-4" style={{ fontSize: '0.9rem' }}>
            Select up to {MAX_INITIATOR_PEDALS} pedals to offer in this trade
            {selectedPedalIds.length > 0 && (
              <span style={{ fontWeight: '600', color: '#3b82f6', marginLeft: '6px' }}>
                ({selectedPedalIds.length} selected)
              </span>
            )}
          </p>

          {hasInterestPicks && (
            // Same treatment as a message in the offer history: their tinted
            // card, avatar and name in the header, and what they said quoted
            // beneath it. This IS a message from them, so it should look like one.
            <div style={{
              maxWidth: '620px', margin: '0 auto 1.25rem', padding: '16px',
              backgroundColor: '#fef9ee', border: '1.5px solid #d4a843',
              borderRadius: '12px', textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <img
                  src={targetOwner?.photoURL || '/images/default-avatar.png'}
                  alt=""
                  style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    objectFit: 'cover', border: '1px solid #e5e7eb', flexShrink: 0
                  }}
                />
                <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                  <strong>{targetOwner?.username || 'They'}</strong> is interested in{' '}
                  {flaggedPedalIds.length === 1
                    ? 'the pedal'
                    : `the ${flaggedPedalIds.length} pedals`}
                  {/* Once the full board is on screen the picks are no longer
                      simply "below", they're the tagged ones among the rest. */}
                  {showAllPedals ? ' tagged below.' : ' below.'}
                </span>
              </div>
              {/* Their terms belong on the screen where the offer is priced, not
                  two taps back on a card. */}
              {interestReply?.note && (
                <p style={{
                  fontSize: '0.95rem', color: '#374151', fontStyle: 'italic',
                  fontWeight: '600', lineHeight: '1.5', margin: '0 0 8px'
                }}>
                  &ldquo;{interestReply.note}&rdquo;
                </p>
              )}
            </div>
          )}

          {hasInterestPicks && (
            // Your side of the conversation, in your color. Keeping your controls
            // out of their card is what makes the gold one read as something they
            // said rather than a panel you operate.
            <div style={{
              maxWidth: '620px', margin: '0 auto 1.25rem', padding: '12px 16px',
              backgroundColor: '#f0f7fc', border: '1.5px solid #5A9ECB',
              borderRadius: '12px', textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <img
                  src={firestoreUser?.photoURL || '/images/default-avatar.png'}
                  alt=""
                  style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    objectFit: 'cover', border: '1px solid #e5e7eb', flexShrink: 0
                  }}
                />
                <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                  Submit a trade offer, or let them know you&apos;re no longer interested
                </span>
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                justifyContent: 'space-between', gap: '12px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowAllPedals((prev) => !prev)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: '#5A9ECB', fontSize: '0.95rem', fontFamily: 'Nunito, sans-serif'
                  }}
                >
                  {showAllPedals ? 'Show only their picks' : 'Show all my pedals'}
                </button>
                {/* They're waiting on an offer that may never come. Walking away
                    has to be as easy as the pass they were given on their side. */}
                {closingInterest ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => setClosingInterest(false)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: '#6b7280', fontSize: '0.95rem', fontFamily: 'Nunito, sans-serif'
                      }}
                    >
                      Never mind
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseInterest}
                      disabled={closeSubmitting}
                      style={{
                        backgroundColor: '#6B7280', color: 'white', border: '1px solid #6B7280',
                        borderRadius: '9999px', padding: '7px 16px',
                        fontFamily: 'Nunito, sans-serif', fontSize: '15px',
                        cursor: closeSubmitting ? 'default' : 'pointer',
                        opacity: closeSubmitting ? 0.6 : 1
                      }}
                    >
                      {closeSubmitting ? 'Sending...' : 'Yes, pass'}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setClosingInterest(true)}
                    style={{
                      marginLeft: 'auto',
                      backgroundColor: 'transparent', color: '#374151',
                      border: '1px solid #9CA3AF', borderRadius: '9999px',
                      padding: '7px 16px', fontFamily: 'Nunito, sans-serif',
                      fontSize: '15px', cursor: 'pointer',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    Not for me, thanks
                  </button>
                )}
              </div>
            </div>
          )}

          {visiblePedals.length === 0 ? (
            <div className="text-center text-gray-500 p-4">
              <p>No available pedals to offer</p>
            </div>
          ) : (
            <div className="your-pedals-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.25rem', maxWidth: '1100px', margin: '0 auto', padding: '0 1rem' }}>
              {[...visiblePedals].sort((a, b) => {
                const aIdx = selectedPedalIds.indexOf(a.id);
                const bIdx = selectedPedalIds.indexOf(b.id);
                if (aIdx !== -1 && bIdx === -1) return -1;
                if (aIdx === -1 && bIdx !== -1) return 1;
                if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                return 0;
              }).map((pedal) => {
                const isSelected = selectedPedalIds.includes(pedal.id);
                const selectionIndex = selectedPedalIds.indexOf(pedal.id);
                const isMaxed = selectedPedalIds.length >= MAX_INITIATOR_PEDALS && !isSelected;
                // Only worth tagging in the full board view. In the filtered one
                // every tile is a pick, so the tag would just be noise.
                const isPick = showAllPedals && flaggedPedalIds.includes(pedal.id);
                return (
                  <div
                    key={pedal.id}
                    id={`pedal-card-${pedal.id}`}
                    className="cursor-pointer"
                    onClick={() => !isMaxed && togglePedalSelection(pedal.id)}
                    style={{
                      position: 'relative',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: isSelected ? '#EFF6FF' : 'white',
                      boxShadow: isSelected
                        ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(59, 130, 246, 0.4)'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease-in-out',
                      textAlign: 'center',
                      opacity: isMaxed ? 0.5 : 1,
                      cursor: isMaxed ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => { if (!isSelected && !isMaxed) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)'; }}
                    onMouseLeave={(e) => { if (!isSelected && !isMaxed) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'; }}
                  >
                    {isSelected && (
                      <div style={{
                        position: 'absolute', top: '6px', left: '6px', zIndex: 2,
                        backgroundColor: '#3b82f6', color: 'white',
                        borderRadius: '50%', width: '24px', height: '24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        {selectionIndex + 1}
                      </div>
                    )}
                    {/* Top right: the selection index already owns the top left. */}
                    {isPick && (
                      <div style={{
                        position: 'absolute', top: '6px', right: '6px', zIndex: 2,
                        backgroundColor: '#d4a843', color: 'white',
                        borderRadius: '9999px', padding: '2px 8px',
                        fontSize: '0.65rem', fontWeight: '700',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        Picked
                      </div>
                    )}
                    {/* Wrapper exists so the balance ribbon can pin to the bottom
                        of the image instead of the bottom of the card. */}
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      {pedal.images?.top ? (
                        <img
                          src={pedal.images.top}
                          alt={`${pedal.make} ${pedal.model}`}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '10px', display: 'block' }}
                        />
                      ) : (
                        <ListingThumb
                          src={placeholderFor(pedal).url}
                          fallbackSrc={SKETCH_PLACEHOLDER_URL}
                          alt='Photos coming'
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '10px', display: 'block', opacity: 0.85 }}
                        />
                      )}
                      {(() => {
                        // What this pedal on its own would mean against their pedal —
                        // the question you're answering on the first pick. Once
                        // anything is selected the running summary owns the verdict,
                        // otherwise per-tile "Even trade" badges contradict a combined
                        // total that is nowhere near even.
                        if (selectedPedalIds.length > 0) return null;
                        const soloBalance = getListedBalance([pedal.price], targetPedal?.price);
                        if (!soloBalance) return null;
                        return (
                          <div className={getBalanceBadgeClass(soloBalance)}>
                            {formatBalanceLabel(soloBalance)}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#374151', fontSize: '0.95rem', lineHeight: '1.2' }}>
                      {pedal.make}
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.2', marginBottom: '4px' }}>
                      {pedal.model}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      <div><strong>Condition:</strong> {pedal.condition}</div>
                      <div><strong>Price:</strong> ${pedal.price}</div>
                    </div>
                    <Link
                      to={`/listing/${pedal.id}`}
                      onClick={(e) => { e.stopPropagation(); saveFormStateToSession(); }}
                      className='hover:underline'
                      style={{ color: '#5A9ECB', fontSize: '0.8rem', marginTop: '4px', display: 'inline-block' }}
                    >
                      View Full Listing
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fixed gap below content */}
        <div style={{ height: '60px' }}></div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="back-to-top-btn"
            style={{
              position: 'fixed', bottom: '30px', right: '30px',
              backgroundColor: '#5A9ECB', color: 'white', border: 'none', borderRadius: '50%',
              width: '36px', height: '36px', fontSize: '16px', fontWeight: 'bold',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', zIndex: 1000,
              transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#4A8AB0'; e.target.style.transform = 'scale(1.1)'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#5A9ECB'; e.target.style.transform = 'scale(1)'; }}
            aria-label="Back to top"
          >
            ⌃
          </button>
        )}

        {/* Footer */}
        <div className="bg-gray-100 border-t border-gray-200 py-4" style={{ paddingBottom: '10px' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Need help or found a bug?{' '}
                <button
                  onClick={() => openContact({ category: 'support' })}
                  className="hover:underline"
                  style={{ color: '#5A9ECB', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                >
                  Contact PXC Support
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Inline shipping-address modal: opens only when the user clicks
            Submit Offer without a complete address. */}
        <AddressRequiredModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          onSaved={() => setShowAddressModal(false)}
          user={firestoreUser}
          context="direct-offer"
        />
      </div>
    </div>
  );
};

export default DirectOffer;
